import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
  Alert,
  AlertDescription,
} from "@tellescope/ui";
import {
  useEnduserOtpMethods,
  useEnduserOtpSentVia,
  useEnduserOtpError,
  useEnduserOtpMode,
  useEnduserSendOtp,
  useEnduserVerifyOtp,
  useEnduserClearOtp,
  useEnduserLogout,
} from "../store/hooks";
import { useIsSMSForMFADisabled } from "../../shared/store/portalSchemaStore";

const RESEND_COOLDOWN_SECONDS = 60;

type OTPStep = "method-selection" | "code-entry";

export const OTPRecoveryPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();

  // Store hooks
  const otpMethods = useEnduserOtpMethods();
  const otpSentVia = useEnduserOtpSentVia();
  const otpError = useEnduserOtpError();
  const otpMode = useEnduserOtpMode();
  const sendOtp = useEnduserSendOtp();
  const verifyOtp = useEnduserVerifyOtp();
  const clearOtp = useEnduserClearOtp();
  const logout = useEnduserLogout();
  const isSMSDisabled = useIsSMSForMFADisabled();

  // Filter out SMS when the portal setting disables it
  const availableMethods = useMemo(
    () => (isSMSDisabled ? otpMethods?.filter((m) => m.toLowerCase() !== "sms") : otpMethods) ?? [],
    [otpMethods, isSMSDisabled]
  );

  // Determine if this is MFA mode based on otpMode from store
  const isMfaMode = otpMode === "mfa";

  // Local state
  const [step, setStep] = useState<OTPStep>("method-selection");
  const [selectedMethod, setSelectedMethod] = useState<string>(() => availableMethods[0] || "");
  const [otpCode, setOtpCode] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendCode = useCallback(async () => {
    if (!selectedMethod) return;

    setIsSending(true);
    const success = await sendOtp(selectedMethod);
    setIsSending(false);

    if (success) {
      setStep("code-entry");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    }
  }, [selectedMethod, sendOtp]);

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0 || !otpSentVia) return;

    setIsSending(true);
    const success = await sendOtp(otpSentVia);
    setIsSending(false);

    if (success) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setOtpCode(""); // Clear previous code
    }
  }, [resendCooldown, otpSentVia, sendOtp]);

  const handleVerifyCode = useCallback(async () => {
    if (otpCode.length !== 6) return;

    setIsVerifying(true);
    const success = await verifyOtp(otpCode);
    setIsVerifying(false);

    if (success) {
      // Session established - navigate to portal home
      navigate("/", { replace: true });
    }
    // If failed, error will be shown from store
  }, [otpCode, verifyOtp, navigate]);

  const handleCancel = useCallback(async () => {
    clearOtp();
    await logout();
    navigate("/login", { replace: true });
  }, [clearOtp, logout, navigate]);

  const handleBackToMethodSelection = useCallback(() => {
    setStep("method-selection");
    setOtpCode("");
  }, []);

  // Format method name for display
  const formatMethodName = (method: string): string => {
    switch (method.toLowerCase()) {
      case "email":
        return t("Email");
      case "sms":
        return t("SMS");
      default:
        return method;
    }
  };

  if (!otpMethods || otpMethods.length === 0) {
    return null; // Should not happen - EnduserProtectedRoute should handle this
  }

  // All methods were filtered out (e.g., only SMS was available but SMS is disabled)
  if (availableMethods.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">{t("Verification Unavailable")}</CardTitle>
            <CardDescription>
              {t(
                "No verification methods are currently available. Please contact your healthcare provider for assistance."
              )}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="ghost" onClick={handleCancel} className="w-full">
              {t("Back to Login")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            {isMfaMode ? t("Additional Security Required") : t("Verify Your Identity")}
          </CardTitle>
          <CardDescription>
            {step === "method-selection"
              ? isMfaMode
                ? t("Complete multi-factor authentication to continue")
                : t("Your access link has expired. Verify your identity to continue.")
              : t("Enter the 6-digit code sent to your {{method}}", { method: formatMethodName(otpSentVia || "") })}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {step === "method-selection" && (
            <>
              <div className="space-y-3">
                <Label>{t("Send code via")}</Label>
                <RadioGroup value={selectedMethod} onValueChange={setSelectedMethod} className="space-y-2">
                  {availableMethods.map((method) => (
                    <div key={method} className="flex items-center space-x-3">
                      <RadioGroupItem value={method} id={`method-${method}`} />
                      <Label htmlFor={`method-${method}`} className="cursor-pointer font-normal">
                        {formatMethodName(method)}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {otpError && (
                <Alert variant="destructive">
                  <AlertDescription>{otpError}</AlertDescription>
                </Alert>
              )}
            </>
          )}

          {step === "code-entry" && (
            <>
              <div className="flex flex-col items-center space-y-4">
                <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {otpError && (
                <Alert variant="destructive">
                  <AlertDescription>{otpError}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-center">
                <Button
                  variant="link"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isSending}
                  className="text-sm"
                >
                  {resendCooldown > 0 ? t("Resend in {{seconds}}s", { seconds: resendCooldown }) : t("Resend Code")}
                </Button>
              </div>
            </>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-3">
          {step === "method-selection" && (
            <>
              <Button onClick={handleSendCode} disabled={!selectedMethod || isSending} className="w-full">
                {isSending ? t("Sending...") : t("Send Code")}
              </Button>
              <Button variant="ghost" onClick={handleCancel} className="w-full">
                {t("Cancel")}
              </Button>
            </>
          )}

          {step === "code-entry" && (
            <>
              <Button onClick={handleVerifyCode} disabled={otpCode.length !== 6 || isVerifying} className="w-full">
                {isVerifying ? t("Verifying...") : t("Verify")}
              </Button>
              <div className="flex w-full gap-2">
                <Button variant="ghost" onClick={handleBackToMethodSelection} className="flex-1">
                  {t("Back")}
                </Button>
                <Button variant="ghost" onClick={handleCancel} className="flex-1">
                  {t("Cancel")}
                </Button>
              </div>
            </>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};
